/**
 * Utility functions for parsing SQL queries, with support for PostgreSQL dollar-quoted strings
 */
import type * as monaco from 'monaco-editor';

export interface QueryBlock {
  query: string
  startLine: number
  endLine: number
  startColumn: number
  endColumn: number
}

/**
 * Parse SQL queries separated by semicolon, with proper support for PostgreSQL dollar-quoted strings
 */
export function parseQueries(sql: string): QueryBlock[] {
  if (!sql.trim()) return []

  const queries: QueryBlock[] = []
  const lines = sql.split('\n')
  let currentQuery = ''
  let queryStartLine = 1
  let actualQueryStartLine = 1 // Track where the actual query content starts
  let queryStartColumn = 1
  let inString = false
  let stringChar = ''
  let inComment = false
  let inDollarQuote = false
  let dollarQuoteTag = ''
  let hasFoundQueryContent = false // Track if we've found actual query content

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex]

    for (let charIndex = 0; charIndex < line.length; charIndex++) {
      const char = line[charIndex]
      const nextChar = line[charIndex + 1]

      // Track the first non-whitespace, non-comment character as the actual query start
      if (!hasFoundQueryContent && char.trim() && !inComment && !inDollarQuote) {
        actualQueryStartLine = lineIndex + 1
        hasFoundQueryContent = true
      }

      // Handle PostgreSQL dollar-quoted strings (e.g., $$, $tag$)
      if (!inString && !inComment && char === '$') {
        if (!inDollarQuote) {
          // Potential start of dollar quote - find the tag
          let tagEnd = charIndex + 1
          while (tagEnd < line.length && line[tagEnd] !== '$') {
            const tagChar = line[tagEnd]
            // Valid tag characters: letters, digits, underscores
            if (!/[a-zA-Z0-9_]/.test(tagChar)) {
              break
            }
            tagEnd++
          }
          
          if (tagEnd < line.length && line[tagEnd] === '$') {
            // Found complete dollar quote tag
            dollarQuoteTag = line.substring(charIndex, tagEnd + 1)
            inDollarQuote = true
            charIndex = tagEnd // Skip to the end of the tag
          }
        } else {
          // Check if this is the end of the current dollar quote
          const remainingLine = line.substring(charIndex)
          if (remainingLine.startsWith(dollarQuoteTag)) {
            inDollarQuote = false
            const tagLength = dollarQuoteTag.length
            // Add the closing tag to currentQuery
            currentQuery += line.substring(charIndex, charIndex + tagLength)
            dollarQuoteTag = ''
            charIndex += tagLength - 1 // Skip the tag (minus 1 because loop will increment)
            continue // Skip the rest of this iteration to avoid reprocessing
          }
        }
      }

      // Handle regular string literals (only if not in dollar quote)
      if (!inDollarQuote && !inComment && (char === '"' || char === "'" || char === '`')) {
        if (!inString) {
          inString = true
          stringChar = char
        } else if (char === stringChar) {
          inString = false
          stringChar = ''
        }
      }

      // Handle comments (only if not in string or dollar quote)
      if (!inString && !inDollarQuote) {
        if (char === '-' && nextChar === '-') {
          inComment = true
        } else if (char === '/' && nextChar === '*') {
          inComment = true
        } else if (inComment && char === '*' && nextChar === '/') {
          inComment = false
          charIndex++ // Skip next character
          continue
        }
      }

      currentQuery += char

      // Check for semicolon (end of query) - only if not in string, comment, or dollar quote
      if (!inString && !inComment && !inDollarQuote && char === ';' && hasFoundQueryContent) {
        const trimmedQuery = currentQuery.trim()
        if (trimmedQuery && trimmedQuery !== ';') {
          const queryBlock = {
            query: trimmedQuery,
            startLine: actualQueryStartLine, // Use the actual query start line
            endLine: lineIndex + 1,
            startColumn: queryStartColumn,
            endColumn: charIndex + 2
          }
          queries.push(queryBlock)
        }

        // Reset for next query
        currentQuery = ''
        queryStartLine = lineIndex + 1
        queryStartColumn = charIndex + 2
        hasFoundQueryContent = false // Reset for next query
        actualQueryStartLine = lineIndex + 1 // Reset actual start line for next query
      }
    }
    
    // Reset comment flag at end of line for single-line comments
    if (inComment) {
      inComment = false
    }
    
    // Add newline character except for the last line
    if (lineIndex < lines.length - 1) {
      currentQuery += '\n'
    }
  }
  
  // Add the last query if it doesn't end with semicolon
  const trimmedQuery = currentQuery.trim()
  if (trimmedQuery) {
    // Find the actual start line for the last query
    if (!hasFoundQueryContent) {
      // Find the first non-empty line in the remaining query
      const queryLines = currentQuery.split('\n')
      let foundStartLine = false
      for (const [i] of queryLines.entries()) {
        if (queryLines[i].trim()) {
          // Calculate the actual line number in the original text
          actualQueryStartLine = queryStartLine + i
          foundStartLine = true
          break
        }
      }
      // If no content found, use the current queryStartLine
      if (!foundStartLine) {
        actualQueryStartLine = queryStartLine
      }
    }

    const queryBlock = {
      query: trimmedQuery,
      startLine: actualQueryStartLine,
      endLine: lines.length,
      startColumn: queryStartColumn,
      endColumn: lines[lines.length - 1].length + 1
    }
    queries.push(queryBlock)
  }
  
  return queries
}

/**
 * Create folding ranges for SQL queries in Monaco editor, supporting PostgreSQL dollar-quoted strings
 */
export function createSqlFoldingRanges(text: string, monacoInstance: typeof monaco): monaco.languages.FoldingRange[] {
  const lines = text.split('\n')
  const foldingRanges: monaco.languages.FoldingRange[] = []
  
  let currentQueryStart = 0
  let inString = false
  let stringChar = ''
  let inComment = false
  let inDollarQuote = false
  let dollarQuoteTag = ''
  let hasFoundQueryContent = false
  
  for (const [lineIndex, line] of lines.entries()) {
    
    for (let charIndex = 0; charIndex < line.length; charIndex++) {
      const char = line[charIndex]
      const nextChar = line[charIndex + 1]
      
      // Track if we've found actual query content (non-whitespace, non-comment)
      if (!hasFoundQueryContent && char.trim() && !inComment && !inDollarQuote) {
        currentQueryStart = lineIndex
        hasFoundQueryContent = true
      }
      
      // Handle PostgreSQL dollar-quoted strings
      if (!inString && !inComment && char === '$') {
        if (!inDollarQuote) {
          // Potential start of dollar quote - find the tag
          let tagEnd = charIndex + 1
          while (tagEnd < line.length && line[tagEnd] !== '$') {
            const tagChar = line[tagEnd]
            // Valid tag characters: letters, digits, underscores
            if (!/[a-zA-Z0-9_]/.test(tagChar)) {
              break
            }
            tagEnd++
          }
          
          if (tagEnd < line.length && line[tagEnd] === '$') {
            // Found complete dollar quote tag
            dollarQuoteTag = line.substring(charIndex, tagEnd + 1)
            inDollarQuote = true
            charIndex = tagEnd // Skip to the end of the tag
          }
        } else {
          // Check if this is the end of the current dollar quote  
          const remainingLine = line.substring(charIndex)
          if (remainingLine.startsWith(dollarQuoteTag)) {
            inDollarQuote = false
            const tagLength = dollarQuoteTag.length
            dollarQuoteTag = ''
            charIndex += tagLength - 1 // Skip the tag (minus 1 because loop will increment)
            continue // Skip the rest of this iteration to avoid reprocessing
          }
        }
      }
      
      // Handle regular string literals (only if not in dollar quote)
      if (!inDollarQuote && !inComment && (char === '"' || char === "'" || char === '`')) {
        if (!inString) {
          inString = true
          stringChar = char
        } else if (char === stringChar) {
          inString = false
          stringChar = ''
        }
      }
      
      // Handle comments (only if not in string or dollar quote)
      if (!inString && !inDollarQuote) {
        if (char === '-' && nextChar === '-') {
          inComment = true
        } else if (char === '/' && nextChar === '*') {
          inComment = true
        } else if (inComment && char === '*' && nextChar === '/') {
          inComment = false
          charIndex++ // Skip next character
          continue
        }
      }
      
      // Check for semicolon (end of query) - only if not in string, comment, or dollar quote
      if (!inString && !inComment && !inDollarQuote && char === ';' && hasFoundQueryContent) {
        // Create folding range if query spans multiple lines
        if (lineIndex > currentQueryStart) {
          foldingRanges.push({
            start: currentQueryStart + 1, // Monaco uses 1-based line numbers
            end: lineIndex + 1,
            kind: monacoInstance.languages.FoldingRangeKind.Region
          })
        }
        
        // Reset for next query
        hasFoundQueryContent = false
        currentQueryStart = lineIndex + 1
      }
    }
    
    // Reset comment flag at end of line for single-line comments
    if (inComment) {
      inComment = false
    }
  }
  
  // Handle the last query if it doesn't end with semicolon and spans multiple lines
  if (hasFoundQueryContent && lines.length - 1 > currentQueryStart) {
    foldingRanges.push({
      start: currentQueryStart + 1,
      end: lines.length,
      kind: monacoInstance.languages.FoldingRangeKind.Region
    })
  }
  
  return foldingRanges
}

/**
 * Strip SQL comments from a query while preserving string literals and PostgreSQL dollar-quoted strings
 */
export function stripComments(sql: string): string {
  if (!sql.trim()) return sql

  let result = ''
  let inString = false
  let stringChar = ''
  let inComment = false
  let commentType = '' // '--' or '/*'
  let inDollarQuote = false
  let dollarQuoteTag = ''
  const lines = sql.split('\n')

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex]

    for (let charIndex = 0; charIndex < line.length; charIndex++) {
      const char = line[charIndex]
      const nextChar = line[charIndex + 1]

      // Handle PostgreSQL dollar-quoted strings (e.g., $$, $tag$)
      if (!inString && !inComment && char === '$') {
        if (!inDollarQuote) {
          // Potential start of dollar quote - find the tag
          let tagEnd = charIndex + 1
          while (tagEnd < line.length && line[tagEnd] !== '$') {
            const tagChar = line[tagEnd]
            // Valid tag characters: letters, digits, underscores
            if (!/[a-zA-Z0-9_]/.test(tagChar)) {
              break
            }
            tagEnd++
          }
          
          if (tagEnd < line.length && line[tagEnd] === '$') {
            // Found complete dollar quote tag
            dollarQuoteTag = line.substring(charIndex, tagEnd + 1)
            inDollarQuote = true
            result += line.substring(charIndex, tagEnd + 1)
            charIndex = tagEnd // Skip to the end of the tag
            continue
          }
        } else {
          // Check if this is the end of the current dollar quote
          const remainingLine = line.substring(charIndex)
          if (remainingLine.startsWith(dollarQuoteTag)) {
            inDollarQuote = false
            const tagLength = dollarQuoteTag.length
            result += line.substring(charIndex, charIndex + tagLength)
            dollarQuoteTag = ''
            charIndex += tagLength - 1 // Skip the tag (minus 1 because loop will increment)
            continue
          }
        }
      }

      // Handle regular string literals (only if not in dollar quote)
      if (!inDollarQuote && !inComment && (char === '"' || char === "'" || char === '`')) {
        if (!inString) {
          inString = true
          stringChar = char
        } else if (char === stringChar) {
          inString = false
          stringChar = ''
        }
        result += char
        continue
      }

      // Handle comments (only if not in string or dollar quote)
      if (!inString && !inDollarQuote) {
        if (char === '-' && nextChar === '-') {
          inComment = true
          commentType = '--'
          charIndex++ // Skip the next character
          continue
        } else if (char === '/' && nextChar === '*') {
          inComment = true
          commentType = '/*'
          charIndex++ // Skip the next character
          continue
        } else if (inComment && commentType === '/*' && char === '*' && nextChar === '/') {
          inComment = false
          commentType = ''
          charIndex++ // Skip next character
          continue
        }
      }

      // Add character to result only if not in comment
      if (!inComment) {
        result += char
      }
    }
    
    // Reset comment flag at end of line for single-line comments
    if (inComment && commentType === '--') {
      inComment = false
      commentType = ''
    }
    
    // Add newline character except for the last line (only if not in comment)
    if (lineIndex < lines.length - 1 && !inComment) {
      result += '\n'
    }
  }
  
  return result.trim()
}